"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.DateRangeFieldLogic = void 0;
const date_fns_1 = require("date-fns");
const moment_timezone_1 = __importDefault(require("moment-timezone"));
const date_1 = require("../../aggregations/date");
const date_range_1 = require("../../aggregations/date-range");
const clipboard_1 = require("../../clipboard");
const utils_1 = require("../../clipboard/utils");
const types_1 = require("@smartsuite/types");
const field_logic_1 = require("../../field-type-registry/field-logic");
const fields_logic_registry_1 = require("../../field-type-registry/fields-logic-registry");
const fields_1 = require("../../utils/fields");
const google_sheet_1 = require("../../utils/google-sheet");
const migration_1 = require("../../utils/migration");
const date_range_2 = require("../../formatters/date-range");
const date_range_fieldtype_1 = require("../../safe-field-value/date-fields/date-range-field/date-range.fieldtype");
const utils_2 = require("../../sort/utils");
const date_2 = require("../../utils/date");
const date_range_3 = require("../../utils/date-range");
const validation_1 = require("../../validation");
const texts_1 = require("../../texts");
const intl_1 = require("../../locale/intl");
const date_3 = require("../../filter/date");
class DateRangeFieldLogic extends field_logic_1.BaseFieldTypeLogic {
    constructor() {
        super(...arguments);
        this.aggregates = {
            empty: { label: (0, intl_1.$t)(texts_1.aggregatesText.empty), aggregator: field_logic_1.countEmpty },
            "non-empty": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.non_empty),
                aggregator: field_logic_1.countFilled,
            },
            "percent-filled": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.percent_filled),
                aggregator: field_logic_1.percentFilled,
            },
            "percent-empty": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.percent_empty),
                aggregator: field_logic_1.percentEmpty,
            },
            "earliest-date": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.earliest_date),
                aggregator: (0, date_range_1.dateRangeAggregate)(date_1.minDate),
            },
            "latest-date": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.latest_date),
                aggregator: (0, date_range_1.dateRangeAggregate)(date_1.maxDate),
            },
            "date-range": {
                label: (0, intl_1.$t)(texts_1.aggregatesText.date_range),
                aggregator: (0, date_range_1.dateRangeAggregate)((dates, ...props) => {
                    if (!dates.length)
                        return null;
                    return `${(0, date_1.minDate)(dates, ...props)} - ${(0, date_1.maxDate)(dates, ...props)}`;
                }),
            },
        };
        this.isDate = true;
        this.isDateRange = true;
        this.name = "daterangefield";
        this.label = (0, intl_1.$t)(texts_1.dateRangeFieldText.date_range);
        this.description = (0, intl_1.$t)(texts_1.dateRangeFieldText.add_a_date_range);
        this.systemLockIcon = true;
        this.format = "YYYY-MM-DD";
        this.icon = "date-range";
        this.iconColor = "#EC506E";
        this.filterComponent = {};
        this.placeholder = (0, intl_1.$t)(texts_1.dateRangeFieldText.select_date_range);
        this.comparisons = {};
        this.fieldValueGetter = new date_range_fieldtype_1.DateRangeFieldValueGetter();
        this.clipboardPasteRules = {
            daterangefield: clipboard_1.clipboardDateRangeToDateRange,
            duedatefield: clipboard_1.clipboardDueDateToDateRange,
            datefield: clipboard_1.clipboardDateToDateRange,
            firstcreatedfield: clipboard_1.clipboardCreatedUpdatedToDateRange,
            lastupdatedfield: clipboard_1.clipboardCreatedUpdatedToDateRange,
            textfield: clipboard_1.clipboardTextToDateRange,
            recordtitlefield: clipboard_1.clipboardTextToDateRange,
            textareafield: clipboard_1.clipboardTextToDateRange,
            unknownfield: clipboard_1.clipboardTextToDateRange,
        };
        this.valueToGoogleSheetArray = (fieldValue, field, userPreferences) => {
            if (this.isValueEmpty(fieldValue)) {
                return [
                    (0, google_sheet_1.makeRowDataValues)("", "stringValue"),
                    (0, google_sheet_1.makeRowDataValues)("", "stringValue"),
                ];
            }
            const formattedValue = (0, date_range_2.getFormattedDateRange)(fieldValue, userPreferences, field.params.display_format, false, true);
            return [
                (0, google_sheet_1.makeRowDataValues)(formattedValue.from_date.date, "stringValue"),
                (0, google_sheet_1.makeRowDataValues)(formattedValue.to_date.date, "stringValue"),
            ];
        };
    }
    get validators() {
        return { requiredDateRange: validation_1.requiredDateRange };
    }
    getDateValueFromString(date, includeTime) {
        const startTime = (0, moment_timezone_1.default)(date).toISOString();
        const endTime = includeTime
            ? (0, moment_timezone_1.default)(startTime).add(1, "hour").toISOString()
            : date;
        return {
            from_date: { date: startTime, include_time: includeTime },
            to_date: { date: endTime, include_time: includeTime },
        };
    }
    addDuration(fieldValue, duration, userPreferences, includeTime) {
        return (0, date_range_3.addDurationToDateRangeValue)(fieldValue, duration, userPreferences, includeTime);
    }
    resolveNestedField(field, slug) {
        const [parentSlug, nestedSlug = "from_date"] = slug.split(".");
        return (0, fields_1.getAppFieldBySlug)([field], `${parentSlug}.${nestedSlug}`);
    }
    getComparisons(field, rule) {
        const nested = this.resolveNestedField(field, rule.field);
        const nestedFieldLogic = (0, fields_logic_registry_1.getFieldLogic)(nested.field_type);
        return nestedFieldLogic.getComparisons(nested, rule);
    }
    processFieldValue(record, slug, comparison, field, userPreferences) {
        const nested = this.resolveNestedField(field, slug);
        const nestedFieldLogic = (0, fields_logic_registry_1.getFieldLogic)(nested.field_type);
        return nestedFieldLogic.processFieldValue(record, slug, comparison, nested, userPreferences);
    }
    processFilterValue(filterValue, comparison, _field, userPreferences) {
        const helper = new date_3.BaseDateModeHelper(comparison, filterValue === null || filterValue === void 0 ? void 0 : filterValue.date_mode, filterValue === null || filterValue === void 0 ? void 0 : filterValue.date_mode_value, userPreferences);
        // @ts-expect-error: Need to resolve
        const valueGetter = helper[filterValue === null || filterValue === void 0 ? void 0 : filterValue.date_mode];
        if (typeof valueGetter !== "function") {
            return filterValue;
        }
        return valueGetter();
    }
    getViewControlRules(field) {
        return {
            ...super.getViewControlRules(field),
            nestedFieldsInFieldsToDisplay: "hide",
            fieldInFilter: "hide",
            fieldInSort: "hide",
            fieldInSpotlight: "hide",
        };
    }
    getInlineEditRules(field) {
        return {
            ...super.getInlineEditRules(field),
            saveKeys: [],
            closeWhenClickOutside: false,
            editorWidth: "100%",
            allowAddRecordToGroup: true,
        };
    }
    sortKey(fieldValue, field, userPreferences, record, structure, rule) {
        const sortByField = this.resolveNestedField(field, rule.field);
        return (0, utils_2.getNestedFieldSortKey)(sortByField, structure, rule, userPreferences, record);
    }
    isValueEqual(fieldValue1, fieldValue2, _field, userPreferences) {
        var _a;
        return (0, date_range_3.isDateRangesEqual)(fieldValue1, fieldValue2, (_a = userPreferences === null || userPreferences === void 0 ? void 0 : userPreferences.timezone.value) !== null && _a !== void 0 ? _a : "");
    }
    valueToString(fieldValue, field, userPreferences, ..._rest) {
        var _a, _b, _c, _d, _e, _f;
        if (!fieldValue ||
            (!((_a = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.from_date) === null || _a === void 0 ? void 0 : _a.date) && !((_b = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.to_date) === null || _b === void 0 ? void 0 : _b.date)))
            return "";
        if (!userPreferences)
            return `${(_c = fieldValue.from_date.date) !== null && _c !== void 0 ? _c : ""} → ${(_d = fieldValue.to_date.date) !== null && _d !== void 0 ? _d : ""}`;
        const formattedValue = (0, date_range_2.getFormattedDateRange)(fieldValue, userPreferences, field.params.display_format);
        return `${(_e = formattedValue.from_date.date) !== null && _e !== void 0 ? _e : ""} → ${(_f = formattedValue.to_date.date) !== null && _f !== void 0 ? _f : ""}`;
    }
    valueToGoogleSheet(fieldValue, field, userPreferences) {
        var _a, _b;
        if (this.isValueEmpty(fieldValue)) {
            return (0, google_sheet_1.makeRowDataValues)("", "stringValue");
        }
        const formattedValue = (0, date_range_2.getFormattedDateRange)(fieldValue, userPreferences, field.params.display_format, false, true);
        const value = `${(_a = formattedValue.from_date.date) !== null && _a !== void 0 ? _a : ""} → ${(_b = formattedValue.to_date.date) !== null && _b !== void 0 ? _b : ""}`;
        return (0, google_sheet_1.makeRowDataValues)(value, "stringValue");
    }
    valueToExport(fieldValue, field, userPreferences) {
        var _a, _b, _c, _d;
        if (!fieldValue ||
            (!((_a = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.from_date) === null || _a === void 0 ? void 0 : _a.date) && !((_b = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.to_date) === null || _b === void 0 ? void 0 : _b.date)))
            return "";
        if (!userPreferences)
            return `${(_c = fieldValue.from_date.date) !== null && _c !== void 0 ? _c : ""}|${(_d = fieldValue.to_date.date) !== null && _d !== void 0 ? _d : ""}`;
        const formattedValue = (0, date_range_2.getFormattedDateRange)(fieldValue, userPreferences, field.params.display_format, false, true);
        return [formattedValue.from_date.date, formattedValue.to_date.date]
            .filter((date) => !!date)
            .join("|");
    }
    sortField(field) {
        var _a, _b;
        return (_b = (_a = field.nested) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : field.params.nested[0];
    }
    filterField(field) {
        var _a, _b;
        const dateFrom = (_b = (_a = field.nested) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : field.params.nested[0];
        return { ...dateFrom, parent: field };
    }
    getEmptyValue() {
        return (0, date_range_3.emptyDateRange)();
    }
    getInitialFieldValue(startValue) {
        return startValue !== null && startValue !== void 0 ? startValue : this.getEmptyValue();
    }
    isValueEmpty(fieldValue) {
        return (0, date_range_3.isEmptyDateRange)(fieldValue);
    }
    prepareValueForClipboard(fieldValue, field, userPreferences) {
        return {
            text: (0, utils_1.dateRangeToClipboardText)(fieldValue, userPreferences, field.params.display_format),
            rawValue: fieldValue,
        };
    }
    getGridColumnSettings(field) {
        return {
            ...super.getGridColumnSettings(field),
            contentAlignment: "centered",
        };
    }
    replaceValue(field, record, sourceValue, targetValue, userPreferences) {
        if (!(0, fields_1.isFieldNested)(field)) {
            sourceValue = sourceValue;
            targetValue = targetValue;
            if (this.isValueEmpty(targetValue)) {
                return targetValue;
            }
            sourceValue = this.isValueEmpty(sourceValue)
                ? this.getEmptyValue()
                : sourceValue;
            const timezone = userPreferences.timezone.value;
            const fromDate = (0, date_2.dateValueToDate)(targetValue.from_date, timezone);
            const toDate = (0, date_2.dateValueToDate)(targetValue.to_date, timezone);
            return {
                from_date: (0, date_2.replaceDate)(sourceValue.from_date, timezone, fromDate === null || fromDate === void 0 ? void 0 : fromDate.getFullYear(), fromDate === null || fromDate === void 0 ? void 0 : fromDate.getMonth(), fromDate === null || fromDate === void 0 ? void 0 : fromDate.getDate()),
                to_date: (0, date_2.replaceDate)(sourceValue.to_date, timezone, toDate === null || toDate === void 0 ? void 0 : toDate.getFullYear(), toDate === null || toDate === void 0 ? void 0 : toDate.getMonth(), toDate === null || toDate === void 0 ? void 0 : toDate.getDate()),
            };
        }
        const dateFieldLogic = (0, fields_logic_registry_1.getFieldLogic)(field.field_type);
        const dateValue = dateFieldLogic.replaceValue(field, record, sourceValue, targetValue, userPreferences);
        const dateRangeValue = (0, fields_1.getValueFromRecordByField)(record, field.parent);
        if (this.isValueEmpty(dateRangeValue)) {
            return { to_date: dateValue, from_date: dateValue };
        }
        if (dateFieldLogic.isValueEmpty(dateValue)) {
            // date range can't have only one nested field empty
            return dateRangeValue;
        }
        return (0, date_range_3.swapDateRangeBoundaries)({ ...dateRangeValue, [field.slug]: dateValue }, userPreferences);
    }
    searchKey(fieldValue, field, userPreferences) {
        return (0, date_range_3.getDateRangeSearchKey)(field, fieldValue, userPreferences);
    }
    groupByKey(fieldValue, field, userPreferences) {
        if (this.isValueEmpty(fieldValue)) {
            return "";
        }
        const fromDate = (0, date_2.dateValueToDate)(fieldValue.from_date, userPreferences.timezone.value);
        const toDate = (0, date_2.dateValueToDate)(fieldValue.to_date, userPreferences.timezone.value);
        const isoFromDate = (0, date_2.formatIsoWithTzOffset)((0, date_fns_1.startOfDay)(fromDate));
        const isoToDate = (0, date_2.formatIsoWithTzOffset)((0, date_fns_1.startOfDay)(toDate));
        return `${isoFromDate} ${isoToDate}`;
    }
    groupBySortKey(value, field, userPreferences) {
        return this.groupByKey(value, field, userPreferences);
    }
    getLinkedRecordFieldSmartWidth(_field) {
        return {
            defaultWidht: 170,
            minWidth: 90,
        };
    }
    getRecordTitleVariableValue(variableName, fieldValue, field, userPreferences) {
        var _a, _b, _c, _d, _e, _f;
        if (!fieldValue ||
            (!((_a = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.from_date) === null || _a === void 0 ? void 0 : _a.date) && !((_b = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.to_date) === null || _b === void 0 ? void 0 : _b.date)))
            return "";
        if (!userPreferences)
            return `${(_c = fieldValue.from_date.date) !== null && _c !== void 0 ? _c : ""} → ${(_d = fieldValue.to_date.date) !== null && _d !== void 0 ? _d : ""}`;
        const formattedValue = (0, date_range_2.getFormattedDateRange)(fieldValue, userPreferences, field.params.display_format, false, true);
        return `${(_e = formattedValue.from_date.date) !== null && _e !== void 0 ? _e : ""} → ${(_f = formattedValue.to_date.date) !== null && _f !== void 0 ? _f : ""}`;
    }
    migrateField(fieldTo, fieldFrom) {
        var _a;
        const migratedField = super.migrateField(fieldTo, fieldFrom);
        return (0, migration_1.migrateDisplayFormat)(migratedField, types_1.dateRangeDisplayFormats, (_a = fieldTo.params.display_format) !== null && _a !== void 0 ? _a : "");
    }
    getEmptyFilterValue(_field, comparison) {
        var _a;
        const comparisonsWithNullValue = ["is_empty", "is_not_empty"].includes(comparison);
        if (comparisonsWithNullValue) {
            return null;
        }
        const dateMode = (_a = this.dateModes[comparison]) !== null && _a !== void 0 ? _a : this.dateModes.default;
        if (!dateMode) {
            return null;
        }
        const defaultDateComparison = dateMode[0];
        return (0, date_3.getDateEmptyFilterValue)(defaultDateComparison.value, defaultDateComparison.label);
    }
    /**
     * Returns date filters for calendar view.
     *
     * @param fieldSlug
     * @param startPeriod
     * @param endPeriod
     */
    getCalendarFilters(fieldSlug, startPeriod, endPeriod) {
        const fromDateSlug = `${fieldSlug}.from_date`;
        const toDateSlug = `${fieldSlug}.to_date`;
        return {
            is_group: true,
            operator: "or",
            conditions: [
                // period overlaps from_date
                // view : |------|
                // value:    |------|
                {
                    is_group: true,
                    operator: "and",
                    conditions: [
                        {
                            field: fromDateSlug,
                            comparison: "is_not_empty",
                            value: null,
                        },
                        {
                            field: fromDateSlug,
                            comparison: "is",
                            value: {
                                date_mode: "date_range",
                                date_mode_value: {
                                    lower: startPeriod,
                                    upper: endPeriod,
                                    bounds: "[]",
                                },
                            },
                        },
                    ],
                },
                // period overlaps to_date
                // view :    |------|
                // value: |------|
                {
                    is_group: true,
                    operator: "and",
                    conditions: [
                        {
                            field: toDateSlug,
                            comparison: "is_not_empty",
                            value: null,
                        },
                        {
                            field: toDateSlug,
                            comparison: "is",
                            value: {
                                date_mode: "date_range",
                                date_mode_value: {
                                    lower: startPeriod,
                                    upper: endPeriod,
                                    bounds: "[]",
                                },
                            },
                        },
                    ],
                },
                // period less than a date range value
                // view :   |--|
                // value: |------|
                {
                    is_group: true,
                    operator: "and",
                    conditions: [
                        {
                            field: toDateSlug,
                            comparison: "is_not_empty",
                            value: null,
                        },
                        {
                            field: fromDateSlug,
                            comparison: "is_not_empty",
                            value: null,
                        },
                        {
                            field: fromDateSlug,
                            comparison: "is_on_or_before",
                            value: {
                                date_mode: "exact_date",
                                date_mode_value: startPeriod,
                            },
                        },
                        {
                            field: toDateSlug,
                            comparison: "is_on_or_after",
                            value: {
                                date_mode: "exact_date",
                                date_mode_value: endPeriod,
                            },
                        },
                    ],
                },
            ],
        };
    }
    filterToRecordValue(filterValue, comparison, field, userPreferences, recordValue) {
        var _a;
        if (comparison !== "is" || !(0, fields_1.isFieldNested)(field)) {
            throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
        }
        recordValue = recordValue !== null && recordValue !== void 0 ? recordValue : this.getEmptyValue();
        const fromDate = recordValue.from_date.date ? recordValue.from_date : null;
        const toDate = recordValue.to_date.date ? recordValue.to_date : null;
        const now = (0, date_2.getNow)(userPreferences.timezone.value);
        const today = (0, date_fns_1.startOfDay)(now);
        if (filterValue.date_mode === "today") {
            const date = (0, date_2.dateToDateValue)(today);
            return {
                from_date: fromDate !== null && fromDate !== void 0 ? fromDate : date,
                to_date: toDate !== null && toDate !== void 0 ? toDate : date,
                [field.slug]: date,
            };
        }
        if (filterValue.date_mode === "tomorrow") {
            const date = (0, date_2.dateToDateValue)((0, date_fns_1.addDays)(today, 1));
            return {
                from_date: fromDate !== null && fromDate !== void 0 ? fromDate : date,
                to_date: toDate !== null && toDate !== void 0 ? toDate : date,
                [field.slug]: date,
            };
        }
        if (filterValue.date_mode === "yesterday") {
            const date = (0, date_2.dateToDateValue)((0, date_fns_1.addDays)(today, -1));
            return {
                from_date: fromDate !== null && fromDate !== void 0 ? fromDate : date,
                to_date: toDate !== null && toDate !== void 0 ? toDate : date,
                [field.slug]: date,
            };
        }
        if ((0, date_3.isDateFilterRangeValue)(filterValue) &&
            ((_a = filterValue.date_mode_value) === null || _a === void 0 ? void 0 : _a.lower)) {
            const isExactDate = filterValue.date_mode_value.lower === filterValue.date_mode_value.upper;
            if (isExactDate) {
                const date = (0, date_2.dateToDateValue)((0, date_fns_1.startOfDay)(new Date(filterValue.date_mode_value.lower)));
                return {
                    from_date: fromDate !== null && fromDate !== void 0 ? fromDate : date,
                    to_date: toDate !== null && toDate !== void 0 ? toDate : date,
                    [field.slug]: date,
                };
            }
        }
        throw new types_1.FilterToRecordValueConversionError(field, filterValue, comparison);
    }
}
exports.DateRangeFieldLogic = DateRangeFieldLogic;
